import { type KeyboardEvent, type RefObject, useLayoutEffect, useRef } from "react";

/**
 * Keyboard model of the sidebar's Projects and Recents (APG disclosure navigation with arrow
 * keys, design §7): their entries are links (middle-click and "open in new tab" keep working),
 * and the whole group is one tab stop.
 *
 * Entries carry `data-nav-item` (their key). A project entry also carries `data-nav-project`
 * and `aria-expanded`; a conversation listed under a project carries `data-nav-parent` (the
 * project's key). In the group:
 * - Up and Down move between visible entries; Home and End go to the first and last;
 * - Right expands a collapsed project, or moves from an expanded one to its first conversation;
 * - Left collapses an expanded project, or moves from a conversation to its project;
 * - Enter follows the focused link (native).
 *
 * An entry's row action (a project's "New conversation", `data-nav-action` naming the entry's
 * key) is not walked by the arrows: it is the next tab stop after its entry while that entry
 * holds the group's tab stop, so Tab reaches it and Shift+Tab comes back.
 */
export function useRovingNav(
  container: RefObject<HTMLElement | null>,
  options: { onExpand: (key: string, expanded: boolean) => void },
) {
  // The entry that holds the group's tab stop (the last one focused).
  const active = useRef<string | null>(null);
  const onExpand = useRef(options.onExpand);
  onExpand.current = options.onExpand;

  // Only rendered entries: a collapsed project renders no conversations.
  const entries = () => [
    ...(container.current?.querySelectorAll<HTMLElement>("[data-nav-item]") ?? []),
  ];
  // Only the tab-stop entry's row action is tabbable.
  const syncActions = (key: string | undefined) => {
    for (const el of container.current?.querySelectorAll<HTMLElement>("[data-nav-action]") ?? []) {
      const index = key !== undefined && el.dataset.navAction === key ? 0 : -1;
      if (el.tabIndex !== index) {
        el.tabIndex = index;
      }
    }
  };

  // Exactly one entry is tabbable: the active one if it is still there, else the current
  // page's entry, else the first. Kept after every render and whenever entries appear later
  // (sections and expanded projects load in their own components).
  useLayoutEffect(() => {
    const sync = () => {
      const all = entries();
      const keep =
        all.find((el) => el.dataset.navItem === active.current) ??
        all.find((el) => el.getAttribute("aria-current") === "page") ??
        all[0];
      for (const el of all) {
        const index = el === keep ? 0 : -1;
        if (el.tabIndex !== index) {
          el.tabIndex = index;
        }
      }
      syncActions(keep?.dataset.navItem);
    };
    sync();
    const root = container.current;
    if (!root || typeof MutationObserver === "undefined") {
      return;
    }
    const observer = new MutationObserver(sync);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-current"],
    });
    return () => observer.disconnect();
  });

  const focus = (el: HTMLElement | undefined) => {
    if (!el) {
      return;
    }
    active.current = el.dataset.navItem ?? null;
    for (const other of entries()) {
      other.tabIndex = other === el ? 0 : -1;
    }
    syncActions(el.dataset.navItem);
    el.focus();
  };

  return {
    onFocus(event: { target: EventTarget | null }) {
      const el = event.target as HTMLElement | null;
      if (el?.dataset?.navItem) {
        active.current = el.dataset.navItem;
        for (const other of entries()) {
          other.tabIndex = other === el ? 0 : -1;
        }
        syncActions(el.dataset.navItem);
      }
    },
    onKeyDown(event: KeyboardEvent<HTMLElement>) {
      const el = event.target as HTMLElement;
      if (!el.dataset?.navItem) {
        return;
      }
      const all = entries();
      const at = all.indexOf(el);
      const project = el.dataset.navProject !== undefined;
      const expanded = el.getAttribute("aria-expanded") === "true";
      switch (event.key) {
        case "ArrowDown":
          focus(all[Math.min(at + 1, all.length - 1)]);
          break;
        case "ArrowUp":
          focus(all[Math.max(at - 1, 0)]);
          break;
        case "Home":
          focus(all[0]);
          break;
        case "End":
          focus(all.at(-1));
          break;
        case "ArrowRight":
          if (project && !expanded) {
            onExpand.current(el.dataset.navItem, true);
          } else if (project) {
            focus(all.find((x) => x.dataset.navParent === el.dataset.navItem));
          } else {
            return;
          }
          break;
        case "ArrowLeft":
          if (project && expanded) {
            onExpand.current(el.dataset.navItem, false);
          } else if (el.dataset.navParent) {
            focus(all.find((x) => x.dataset.navItem === el.dataset.navParent));
          } else {
            return;
          }
          break;
        default:
          return;
      }
      event.preventDefault();
    },
  };
}
